import { Alert, Badge, Divider, Select, Tooltip } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import React from 'react';

import type { ActionType } from '../MergeAction/MergeAction';

import './Action.scss';

const { Option, OptGroup } = Select;

type ActionsProps = {
  actionList?: ActionType[];
  entityLabel: string;
  onChange?: (value: string | number) => void;
  differingFields?: string[];
  value?: string | number;
  hasBooking?: boolean;
};

export const Action = ({
  actionList,
  onChange,
  entityLabel,
  differingFields,
  value,
  hasBooking,
}: ActionsProps) => {
  const showAlert = differingFields && differingFields.length > 0;

  const getTextContent = (element: React.ReactNode): string => {
    if (typeof element === 'string' || typeof element === 'number') {
      return String(element);
    }

    if (React.isValidElement(element) && element.props) {
      const children = (element.props as { children?: React.ReactNode })
        .children;

      if (Array.isArray(children)) {
        return children.map(getTextContent).join(' ');
      }

      return getTextContent(children);
    }

    return '';
  };

  const searchInSelectOptions = (
    input: string,
    option: DefaultOptionType | undefined
  ) => {
    if (!input) {
      return true;
    }

    const searchText = (text: unknown): boolean => {
      if (typeof text === 'string') {
        return text.toLowerCase().includes(input.toLowerCase());
      }

      if (React.isValidElement(text)) {
        return getTextContent(text).toLowerCase().includes(input.toLowerCase());
      }

      return false;
    };

    return searchText(option?.label) || searchText(option?.children);
  };

  const renderCommonAction = () => {
    return (
      <>
        <Option
          value="CREATE_NEW"
          label="Create it in the parent hotel"
          className="action__selector__option"
        >
          Create it in the parent hotel
        </Option>
        <Option
          value="DO_NOT_MERGE"
          label="Do not merge"
          className="action__selector__option"
          disabled={hasBooking}
        >
          <Tooltip
            title={
              hasBooking
                ? 'Cannot exclude this item because it has existing bookings'
                : undefined
            }
          >
            Do not merge
          </Tooltip>
        </Option>
      </>
    );
  };

  return (
    <div className="action">
      <div className="action__text">Action</div>
      <div className="action__selector">
        <Select
          onChange={onChange}
          placeholder="Select an action"
          optionLabelProp="label"
          value={value}
          showSearch
          filterOption={searchInSelectOptions}
        >
          {!!actionList?.length ? (
            <>
              <OptGroup
                label={`Merge with existing ${entityLabel.toLowerCase()}`}
              >
                {actionList?.map((item) => (
                  <Option
                    key={item.value}
                    value={item.value}
                    label={
                      <Tooltip title={`Merge with ${item.label}`}>
                        <span>{`Merge with ${item.label}`}</span>
                      </Tooltip>
                    }
                    className="action__selector__option"
                    disabled={item.disabled}
                  >
                    <Tooltip
                      title={item.label}
                      className="action__selector__option__container"
                    >
                      <div className="option-label">{item.label}</div>
                      <div className="option-status">
                        <Badge
                          status={item.isPublished ? 'success' : 'error'}
                        />
                        {item.isPublished ? 'Published' : 'Unpublished'}
                      </div>
                    </Tooltip>
                  </Option>
                ))}
              </OptGroup>

              <OptGroup label={<Divider style={{ margin: '8px 0' }} />}>
                {renderCommonAction()}
              </OptGroup>
            </>
          ) : (
            renderCommonAction()
          )}
        </Select>
      </div>
      {showAlert && (
        <Alert
          className="action__alert"
          message={
            <>
              Some information differs between the child hotel and the parent
              hotel. The child hotel information will be overridden:{' '}
              <span className="overridden-fields">
                {differingFields?.join(', ')}
              </span>
            </>
          }
          type="info"
          showIcon
        />
      )}
    </div>
  );
};
